/**
 * SSO Management API (backend spec)
 *
 * PATCH /sso/configurations/{organizationId}/manage
 * Request body: { ssoEnabled: boolean, ssoExcludeRequiredRoles: string[] }
 *
 * UI form uses ssoEnabled (boolean) and ssoUsers; map to API as:
 * - ssoEnabled → ssoEnabled (direct)
 * - ssoUsers "all" → ssoExcludeRequiredRoles [], "all_except_org_admin" → ["ORGANIZATION_ADMIN"], "no_users" → null
 */

export enum SSO_USERS {
  ALL = "all",
  ALL_EXCEPT_ORG_ADMIN = "all_except_org_admin",
  NO_USERS = "no_users",
}

export const SSO_USERS_OPTIONS = [
  {
    label: "No users in this organization",
    value: SSO_USERS.NO_USERS,
    helperLabel:
      "Users will be able to sign in with their email and password or with SSO authentication",
  },
  {
    label: "All users",
    value: SSO_USERS.ALL,
    helperLabel:
      "All users will be required to sign in with SSO authentication",
  },
  {
    label: "All users, except Organization Admin users",
    value: SSO_USERS.ALL_EXCEPT_ORG_ADMIN,
    helperLabel:
      "All users, except Organization Admins, will be required to sign in with SSO authentication",
  },
];

export const SSO_MANAGEMENT_FORM_FIELDS = {
  SSO_ENABLED: "ssoEnabled",
  SSO_USERS: "ssoExcludeRequiredRoles",
} as const;

export const SUPPORTED_IDENTITY_PROVIDERS = ["azure", "onelogin"];

export const IDP_CLIENT_OPTIONS = [
  {label: "OneLogin", value: "onelogin"},
  {label: "Microsoft Azure (Entra ID)", value: "azure"},
];

export const IDP_CLIENT_FIELD_LABELS = {
  onelogin: {
    issuerUrl: "Issuer URL",
    samlEndpoint: "SAML 2.0 Endpoint (HTTP)",
    sloEndpoint: "SLO Endpoint (HTTP)",
  },
  azure: {
    issuerUrl: "Microsoft Entra Identifier",
    samlEndpoint: "Login URL",
    sloEndpoint: "Logout URL",
  },
} as const;

export const SSO_CONFIGURATION_ERRORS = {
  IDP_CLIENT: "IDP CLIENT is required",
  CERTIFICATE: "x.509 certificate is required",
  ISSUER_URL: "Issuer URL is required",
  SAML_ENDPOINT: "SAML 2.0 Endpoint (HTTP) is required",
  SLO_ENDPOINT: "SLO Endpoint (HTTP) is required",

  IDP_CLIENT_INVALID: `IdP Client must be one of the supported providers: ${SUPPORTED_IDENTITY_PROVIDERS.join(
    ", ",
  )}`,
  CERTIFICATE_INVALID: "x.509 certificate is invalid",
  ISSUER_URL_INVALID: "Issuer URL must be a valid http(s) URL",
  SAML_ENDPOINT_INVALID: "SAML 2.0 Endpoint must be a valid http(s) URL",
  SLO_ENDPOINT_INVALID: "SLO Endpoint must be a valid http(s) URL",
}; // end error constants
